import { GroupPosition, GroupStatus } from '@/configs/apis/groups/interface';
import { useChatContext } from '@/contexts/chat/context';
import type { Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { useSessionContext } from '@/contexts/session/context';

const useGroupInfo = (chat: Chat) => {
  const { session } = useSessionContext();
  const { chatGroup } = useChatContext();

  // TODO: add settingsMap
  const { membersMap, groupMap } = chatGroup.chatGroupState.state;

  const group = useMemo(() => groupMap[chat.chat_id], [groupMap, chat.chat_id]);

  const members = useMemo(
    () => membersMap[chat.chat_id]?.members,
    [membersMap, chat.chat_id]
  );

  const normalMembers = useMemo(
    () => membersMap[chat.chat_id]?.normalMembers,
    [membersMap, chat.chat_id]
  );

  const isGroupDisband = useMemo(() => {
    return group?.status === GroupStatus.DISBAND;
  }, [group]);

  const groupOwner = useMemo(
    () => normalMembers?.find((item) => item.role === GroupPosition.Owner),
    [normalMembers]
  );

  const groupManagers = useMemo(
    () => normalMembers?.filter((item) => item.role === GroupPosition.Manager),
    [normalMembers]
  );

  const isGroupOwner = useCallback(
    (userId?: string) => {
      if (userId) return groupOwner?.user_id === userId;
      return groupOwner?.user_id === session.user?.userId;
    },
    [groupOwner?.user_id, session.user?.userId]
  );

  const isGroupManager = useCallback(
    (userId?: string) => {
      if (userId) return groupManagers?.some((item) => item.user_id === userId);
      return groupManagers?.some((item) => item.user_id === session.user?.userId);
    },
    [groupManagers, session.user?.userId]
  );

  const isGroupAdmin = useCallback(
    (userId?: string) => {
      if (userId) return isGroupOwner(userId) && isGroupManager(userId);
      return isGroupOwner(session.user?.userId) || isGroupManager(session.user?.userId);
    },
    [isGroupManager, isGroupOwner, session.user?.userId]
  );

  const isInGroup = useCallback(
    (userId?: string) => {
      if (userId) return normalMembers?.some((item) => item.user_id === userId);
      return normalMembers?.some((item) => item.user_id === session.user?.userId);
    },
    [normalMembers, session.user?.userId]
  );

  return {
    groupOwner,
    groupManagers,
    /** Include members that have already been deleted */
    members,
    normalMembers,
    isGroupOwner,
    isGroupManager,
    isGroupAdmin,
    isInGroup,
    isGroupDisband,
  };
};

export default useGroupInfo;
